import { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import BranchSelect from './Components/BranchSelect';
import { useImmer } from 'use-immer';
import CompositionEditor from './Components/CompositionEditor';
import CompositionInfo from './Components/CompositionInfo';
import ScalarFields from './Components/ScalarFields';
import UnitedAtomDictEditor from './Components/UnitedAtomDictEditor';
import CreateInfoFile from './Utils/CreateInfoFile';
import fieldConfig, { dropdownOptions } from './Components/FieldConfig';
import getInitialData  from './Utils/Data';
import Information from './Components/Information';

export default function App() {
  const OAUTH_ClientID = process.env.REACT_APP_OAUTH_CLIENT_ID;
  const DataRepo = process.env.REACT_APP_DATA_REPO;
  const GITHUB_GATEWAY_PATH = '/app/';
  const API_PATH = '/api/';
  const [loggedIn, setLoggedIn] = useState(false);
  const [activePane, setActivePane] = useState('upload');
  const [userName, setUserName] = useState('');
  const [branch, setBranch] = useState('main');
  const [message, setMessage] = useState('Fill in the form');
  const [pullRequestUrl, setPullRequestUrl] = useState(null);
  const [lipidList, setLipidList] = useState([]);
  const [solutionList, setSolutionList] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [mappingDict,setMappingDict] = useState(
  JSON.parse(localStorage.getItem('mappingDict')) || {});
  const [data,setData] = useImmer(getInitialData());
  const resetData = () => setData(getInitialData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSavedFingerprint, setLastSavedFingerprint] = useState('');
  // Fetch the up‐to‐date molecule list on mount:
 useEffect(() => {
    axios.get(`${API_PATH}molecules`)
      .then(res => {
      setLipidList(res.data.lipids)
      setSolutionList(res.data.solution)
     })
      .catch(err => console.error("Failed to load molecules:", err));
  }, []);
// Fetch mapping files on mount
useEffect(() => {
  axios.get(`${API_PATH}mapping-files`)
    .then(res => {
      localStorage.setItem('mappingDict', JSON.stringify(res.data)); 
      setMappingDict(res.data);
    })
    .catch(err => console.error("Failed to load mappings:", err));
}, []);  

// Method to handle changes in form fields:
const handleChange = e => {
  const { name, value } = e.target;
  const { type, trim } = fieldConfig[name];
  let val = value;
  if (trim) val = val.trim();
  let parsed = val;
  if (type === 'integer') parsed = val === '' ? '' : parseInt(val, 10);
  else if (type === 'float') parsed = val === '' ? '' : parseFloat(val);
  setData(draft => { draft[name] = parsed; });
};


// Handles process after url has been redirected back from Github:
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (localStorage.githubToken) {
    setLoggedIn(true);
    return;
  }
  if (code) {
    axios.post(`${GITHUB_GATEWAY_PATH}verifyCode`, { code })
      .then(res => {
        if (res.data.authenticated) {
          const { token, username } = res.data;
          localStorage.githubToken = token;
          localStorage.username   = username;
          setLoggedIn(true);
          window.history.replaceState(null, '', window.location.pathname);
        }
      })
      .catch(() => setMessage('GitHub login failed'));
  }
}, []);
// Starts login process:
  const githubLogin = () => {
    window.location.assign(`https://github.com/login/oauth/authorize/?client_id=${OAUTH_ClientID}`);
  };
// Handles logout process:
  const handleLogout = () => {
    localStorage.clear();
    setLoggedIn(false);
    setMessage('Fill in the form');
    setUserName('');
    setBranch('main');
    setPullRequestUrl(null);
    setUploadStatus(null);
    resetData();
    setIsSubmitting(false);
    setLastSavedFingerprint('');
    setActivePane('upload');
  }
    
// Handles form submission:
const handleSubmit = async e => {
  e.preventDefault();
  if (isSubmitting) return;

  const hasLipids   = Object.keys(data.LIPID_COMPOSITION).length > 0;
  const hasSolution = Object.keys(data.SOLUTION_COMPOSITION).length > 0;

  if (!hasLipids || !hasSolution) {
    setUploadStatus("Add at least one membrane and one solution molecule."); 
    return;
  }

  let infoFile;
  try {
    infoFile = CreateInfoFile(data);
  } catch (err) {
    console.error('CreateInfoFile error:', err);
    setUploadStatus(err.message || "Failed to create INFO file.");
    return; 
  }
  
  // Creates JSON payload from data
  const jsonPayload = {
    ...infoFile,
    userName,
    branch
  };
  const fingerprint = JSON.stringify(jsonPayload);
  if (fingerprint === lastSavedFingerprint) {
    setUploadStatus("No changes since last successful submit.");
    return;
  }


  setUploadStatus('Uploading data…');
  setIsSubmitting(true);


  try {
    const resp = await axios.post('/app/upload', jsonPayload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.githubToken}`
      }
    });
    setUploadStatus('Upload succeeded!');
    setLastSavedFingerprint(fingerprint);
    
    if (resp.data.pullUrl) {
      setPullRequestUrl(resp.data.pullUrl);
    }

  } catch (err) {
    const errorMsg = err.response?.data?.error ?? err.message;
    console.error('Upload error', err.response?.status, err.response?.data || err);
    setUploadStatus(`Upload failed: ${errorMsg}`);
  }
  finally {
    setIsSubmitting(false);
  }
};

return (
<div className="Container">
  <div className="Left">
  </div>

    <div className="App">
      {loggedIn && (
        <button onClick={handleLogout} className="button secondary logout-button">
          Logout
        </button>
      )}
      <header className="App-header">
        <h1>FAIRMD Upload Portal</h1>
      </header>
          {!loggedIn && (
            <section className="information-pane logged-out-pane">
              <div className="login-panel">
                <div>
                  <h2>Sign in to upload data</h2>
                  <p>
                    Uploads are handled through GitHub so submissions can be reviewed as pull requests.
                  </p>
                </div>
                <button
                  onClick={githubLogin}
                  className="button github-login centered"
                >
                  <svg
                    aria-hidden="true"
                    className="github-logo"
                    viewBox="0 0 16 16"
                    width="20"
                    height="20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82A7.65 7.65 0 0 1 8 3.87c.68 0 1.36.09 2 .26 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z"
                    />
                  </svg>
                  Sign in with GitHub
                </button>
                <div className="info-block">
                  <p>GitHub login is used to verify your identity and reduce spam. The app only uses your public GitHub username.</p>
                </div>
              </div>
              <div className="logged-out-summary">
                <h3>How it works</h3>
                <p>Fill in the upload form, submit your simulation information, and follow the generated pull request for review.</p>
              </div>
            </section>
      )}

      {loggedIn && (
        <>
          <div className="top-actions">
            <nav className="pane-selector" aria-label="Portal sections">
              <button
                type="button"
                className={activePane === 'upload' ? 'active' : ''}
                aria-pressed={activePane === 'upload'}
                onClick={() => setActivePane('upload')}
              >
                Upload
              </button>
              <button
                type="button"
                className={activePane === 'information' ? 'active' : ''}
                aria-pressed={activePane === 'information'}
                onClick={() => setActivePane('information')}
              >
                Information
              </button>
            </nav>
          </div>

          {activePane === 'upload' && (
            <>
          <div className="upload-controls">
            <input
              type="text"
              placeholder="Enter your name"
              value={userName}
              onChange={e => setUserName(e.target.value)}
              className="name-input centered"
            />
            <BranchSelect
              selectedBranch={branch}
              setSelectedBranch={setBranch}
              setMessage={setMessage}
              DataRepo={DataRepo}
            />
            {message && <p className="status-message-centered">Status: {message}</p>}
          </div>

          <form onSubmit={handleSubmit} className="upload-form">
              <ScalarFields
                data={data}
                onChange={handleChange}
                fieldConfig={fieldConfig}
                dropdownOptions={dropdownOptions}
                />
              <UnitedAtomDictEditor data={data} setData={setData} />
              <CompositionInfo/>
              <CompositionEditor
                options={lipidList}
                mappingDict={mappingDict}  
                composition={data.LIPID_COMPOSITION}
                setComposition={recipe => setData(draft => { recipe(draft.LIPID_COMPOSITION); })}
                name="Membrane Composition"
              />
                <CompositionEditor
                options={solutionList}
                mappingDict={mappingDict}  
                composition={data.SOLUTION_COMPOSITION}
                setComposition={recipe => setData(draft => { recipe(draft.SOLUTION_COMPOSITION); })
                }
                name="Solution Composition"
              />
              <div className="submit-row">
                <button type="submit" className="button" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting…" : "Submit"}
                </button>
               <p className="upload-status">
                {uploadStatus}
                </p>
                {pullRequestUrl && (
                  <a
                    href={pullRequestUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button"
                  >View Pull Request</a>
                )}
              </div>
          </form>
                {/* Simple text link to docs */}
         <p className="docs-link">
          View full documentation on{' '}
          <a
            href="https://databank.readthedocs.io/stable/schemas/simulation_metadata.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            FAIRMD lipid metadata
          </a>.
        </p>
            </>
          )}

          {activePane === 'information' && (
            <section className="information-pane">
              <Information />
            </section>
          )}

        </>
      )}
    </div>

</div>
);
}
